import React from "react";
import { useHistory } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { Layout } from "./components/Layout";
import { Introduction } from "./components/Introduction";
import { Fieldset } from "./components/Fieldset";
import { DocumentAcknowledgment } from "./components/DocumentAcknowledgment";
import styles from "./ConfirmInsuranceSkip.module.scss";
import { DocumentType } from "./types";
import { Button } from "components/Button";
import { skipInsuranceCoverageRequest } from "api/skipInsuranceCoverageRequest";
import { Navbar } from "features/checkIn/components/Navbar";
import { useCurrentPatient } from "features/patientDashboard/hooks/useCurrentPatient";
import { useCurrentAppointment } from "features/patientDashboard/hooks/useCurrentAppointment";
import { useErrorToast } from "features/patientDashboard/hooks/useErrorToast";
import { checkInHome } from "features/routing/paths";
import {
  useCheckInPageTracking,
  useCheckInNavigationTracking,
  useCheckInTracking,
} from "features/checkIn/hooks/useCheckInTracking";

export const ConfirmInsuranceSkip: React.FC = () => {
    const { fetchCurrentAppointment } = useCurrentAppointment();
  useCheckInPageTracking({ step: 5, page: "insurance_skip" });
  const hasSubmittedRef = React.useRef(false);
  useCheckInNavigationTracking(
    { step: 5, page: "insurance_skip" },
    hasSubmittedRef
  );
  const { trackCheckInEvent } = useCheckInTracking({
    step: 5,
    page: "insurance_skip",
  });
  const event = (action: string) => `step_5_dci_${action}`;
  const insuranceReviewFormMethods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = insuranceReviewFormMethods;
  const history = useHistory();
  const { handleUnauthorized } = useCurrentPatient();
  const { showErrorToast } = useErrorToast();

  const onSubmit = async () => {
    await handleUnauthorized(async () => {
      try {
        await skipInsuranceCoverageRequest();
        await fetchCurrentAppointment();
        history.push(checkInHome());
      } catch {
        hasSubmittedRef.current = false;
        trackCheckInEvent("api_failure", {
          api: "skip_insurance_coverage",
        });
        showErrorToast();
      }
    });
  };

  return (
    <FormProvider {...insuranceReviewFormMethods}>
      <Layout
        navbar={<Navbar title="Insurance" />}
        primaryCta={
          <Button
            onClick={handleSubmit(
              async () => {
                hasSubmittedRef.current = true;
                trackCheckInEvent(event("save_continue"));
                await onSubmit();
              },
              () => {
                trackCheckInEvent("validation_error");
              }
            )}
            renderAsDisabled={!isValid}
            loading={isSubmitting}
            disabled={isSubmitting}
            text="Save & Continue"
          />
        }
        emptyHeight={true}
      >
        <Introduction header="Review payment terms">
          We want to ensure you&apos;re notified on our policies around payment.
          Our office may follow up with you to ensure your status regarding
          Insurance.
        </Introduction>

        <section className={styles.section}>
          <h2 className={styles.sectionHeader}>Invoice Statements</h2>
          <p className={styles.sectionText}>
            We&apos;ll work with you to resolve any outstanding balance. We send
            statements to patients if there&apos;s anything that has not been
            resolved. Unresolved balances may go through a collection process,
            which are subject to fees.
          </p>
        </section>

        <hr className={styles.separator} />
        <section className={styles.agreementSection}>
          <Fieldset
            legend="Insurance Assignment Policy & Agreement"
            legendClassName={styles.alwaysVisible}
          >
            <DocumentAcknowledgment
              documentType={DocumentType.InsuranceAssignmentPolicy}
              trackingContext={{ step: 5, page: "insurance_skip" }}
            />
          </Fieldset>
        </section>
      </Layout>
    </FormProvider>
  );
};
